import { useMemo } from 'react';
import { formatDisplayDate } from '@/utils/dateUtils';
import { useGetAllUsersAndLinksPagedQuery } from '../../../hooks/queries/useAdminQueries';

export interface User {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  country: string;
  city: string;
  mobileNumber?: string;
  countryCode?: string;
  clicks: number;
  role: string;
  brand: string;
  status?: string;
  profileImage?: string;
  createdAt: string;
  links: Link[];
  totalLinks?: number;
  totalClicks?: number;
  isWhitelisted?: boolean;
  linkCount?: number;
}

export interface Link {
  originalUrl: string;
  shortUrl: string;
  targetSite: string;
  clickCount: number;
}

export interface UserColumn {
  key: string;
  title: string;
  dataIndex: string;
  width: string;
  headerAlign: string;
  cellAlign: string;
  render?: (value: any, user: User) => React.ReactNode;
}

export const useUserColumns = (): UserColumn[] => {
  return useMemo(
    () => [
      {
        key: 'user',
        title: 'User',
        dataIndex: 'user',
        width: '25%',
        headerAlign: 'left',
        cellAlign: 'left',
        render: (_text: string, user: User) => {
          const fullName =
            `${user.firstName || ''} ${user.lastName || ''}`.trim() ||
            'Unknown User';
          return (
            <div className="flex items-center gap-3">
              <img
                src={user.profileImage || '/assets/profileImg.svg'}
                alt={fullName}
                className="w-[40px] h-[40px] rounded-full object-cover border-2 border-[#E5E9EB]"
              />
              <div className="flex flex-col">
                <span className="text-sm font-medium text-[#292828]">
                  {fullName}
                </span>
                <span className="text-xs text-[#9B919D]">{user.email}</span>
              </div>
            </div>
          );
        },
      },
      {
        key: 'status',
        title: 'Status',
        dataIndex: 'status',
        width: '10%',
        headerAlign: 'center',
        cellAlign: 'center',
        render: (_text: string, user: User) => {
          const status = user.status || 'unknown';
          const statusConfig = {
            active: {
              bg: 'bg-[#E6F8F0]',
              text: 'text-[#00AE3E]',
              label: 'Active',
            },
            pending: {
              bg: 'bg-[#FFF6F2]',
              text: 'text-[#FFA500]',
              label: 'Pending',
            },
            inactive: {
              bg: 'bg-[#F3F3F3]',
              text: 'text-[#808080]',
              label: 'Inactive',
            },
            blocked: {
              bg: 'bg-[#FFE5E5]',
              text: 'text-[#DC2626]',
              label: 'Blocked',
            },
          };
          const config =
            statusConfig[status as keyof typeof statusConfig] ||
            statusConfig.inactive;
          return (
            <span
              className={`px-3 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}
            >
              {config.label}
            </span>
          );
        },
      },
      {
        key: 'links',
        title: 'Links',
        dataIndex: 'links.length',
        width: '10%',
        headerAlign: 'center',
        cellAlign: 'center',
        render: (_text: number, user: User) => {
          const linkCount = user.links?.length || 0;
          return (
            <div className="flex items-center justify-center gap-1">
              <img
                src="/assets/linkIcon.svg"
                alt="Links"
                className="w-[16px] h-[16px]"
              />
              <span className="text-sm font-medium text-[#292828]">
                {linkCount}
              </span>
            </div>
          );
        },
      },
      {
        key: 'totalClicks',
        title: 'Clicks',
        dataIndex: 'totalClicks',
        width: '10%',
        headerAlign: 'center',
        cellAlign: 'center',
        render: (_text: number, user: User) => {
          const totalClicks = user.links?.reduce(
            (sum, link) => sum + link.clickCount,
            0,
          );
          return (
            <div className="flex items-center justify-center gap-1">
              <img
                src="/assets/analyticsIcon.svg"
                alt="Clicks"
                className="w-[16px] h-[16px]"
              />
              <span className="text-sm font-medium text-[#292828]">
                {totalClicks || 0}
              </span>
            </div>
          );
        },
      },
      {
        key: 'mobileNumber',
        title: 'Phone',
        dataIndex: 'mobileNumber',
        width: '12%',
        headerAlign: 'center',
        cellAlign: 'center',
        render: (_text: string, user: User) => {
          const phone =
            user.countryCode && user.mobileNumber
              ? `${user.countryCode} ${user.mobileNumber}`
              : user.mobileNumber || '-';
          return <span className="text-sm text-[#292828]">{phone}</span>;
        },
      },
      {
        key: 'location',
        title: 'Location',
        dataIndex: 'location',
        width: '15%',
        headerAlign: 'center',
        cellAlign: 'center',
        render: (_text: string, user: User) => {
          if (!user.country && !user.city) {
            return <span className="text-sm text-[#9B919D]">-</span>;
          }
          return (
            <div className="flex items-center justify-center gap-1">
              <img
                src="/assets/location.svg"
                alt="Location"
                className="w-[14px] h-[14px]"
              />
              <span className="text-sm text-[#292828]">
                {[user.city, user.country].filter(Boolean).join(', ')}
              </span>
            </div>
          );
        },
      },
      {
        key: 'role',
        title: 'Role',
        dataIndex: 'role',
        width: '8%',
        headerAlign: 'center',
        cellAlign: 'center',
        render: (_text: string, user: User) => {
          const role = user.role || 'user';
          return (
            <span className="text-sm text-[#292828] capitalize">{role}</span>
          );
        },
      },
      {
        key: 'whitelist',
        title: 'Whitelist',
        dataIndex: 'isWhitelisted',
        width: '10%',
        headerAlign: 'center',
        cellAlign: 'center',
        render: (_text: boolean, user: User) => {
          return user.isWhitelisted ? (
            <div className="flex items-center justify-center gap-1">
              <img
                src="/assets/whiteCheck.svg"
                alt="Whitelisted"
                className="w-[16px] h-[16px]"
              />
              <span className="text-sm font-medium text-[#00AE3E]">Yes</span>
            </div>
          ) : (
            <div className="flex items-center justify-center gap-1">
              <img
                src="/assets/close.svg"
                alt="Not Whitelisted"
                className="w-[16px] h-[16px]"
              />
              <span className="text-sm text-[#9B919D]">No</span>
            </div>
          );
        },
      },
      {
        key: 'createdAt',
        title: 'Joined',
        dataIndex: 'createdAt',
        width: '10%',
        headerAlign: 'center',
        cellAlign: 'center',
        render: (text: string) => (
          <span className="text-sm text-[#9B919D]">
            {formatDisplayDate(text)}
          </span>
        ),
      },
    ],
    [],
  );
};

export const useUsersData = (
  filter: string,
  page: number,
  limit: number,
  sortField: string,
  sortOrder: string,
) => {
  const { data, isLoading, isError, refetch } =
    useGetAllUsersAndLinksPagedQuery(filter, page, limit, sortField, sortOrder);

  return {
    users: (data?.data ?? []) as User[],
    loading: isLoading,
    error: isError ? 'Failed to load users' : null,
    totalPages: data?.pagination?.totalPages ?? 1,
    totalUsers: data?.pagination?.totalUsers ?? 0,
    fetchUsers: refetch,
  };
};
